/* © NXAPP Limited */
import { useMemo, useState } from 'react'
import { BookOpen, Search, ChevronRight } from 'lucide-react'
import Markdown from '@/components/ui/Markdown'
import { ARTICLES, CATEGORIES, type Article } from './guideContent'

export default function GuidePage() {
  const [query, setQuery] = useState('')
  const [openId, setOpenId] = useState<string | null>(ARTICLES[0]?.id ?? null)

  const results = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return ARTICLES
    return ARTICLES.filter(a =>
      a.title.toLowerCase().includes(q) ||
      a.summary.toLowerCase().includes(q) ||
      a.keywords.some(k => k.includes(q)) ||
      a.body.toLowerCase().includes(q))
  }, [query])

  const open = ARTICLES.find(a => a.id === openId) ?? results[0] ?? null
  const byCategory = (cat: string) => results.filter(a => a.category === cat)

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-gray-900 flex items-center gap-2"><BookOpen className="w-5 h-5 text-gray-500" /> Guide &amp; Knowledge Base</h1>
        <p className="text-sm text-gray-500 mt-1">Step-by-step help for every part of NXBulletin. Search or browse by section.</p>
      </div>

      <div className="relative max-w-md">
        <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search the guide…"
          className="w-full rounded-lg border border-gray-300 pl-9 pr-3 py-2 text-sm" />
      </div>

      <div className="grid lg:grid-cols-[280px_1fr] gap-4 items-start">
        {/* Nav */}
        <nav className="space-y-4">
          {CATEGORIES.map(cat => {
            const arts = byCategory(cat)
            if (!arts.length) return null
            return (
              <div key={cat}>
                <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1">{cat}</p>
                <div className="space-y-0.5">
                  {arts.map(a => (
                    <button key={a.id} onClick={() => setOpenId(a.id)}
                      className={`w-full text-left px-2.5 py-1.5 rounded-md text-sm flex items-center justify-between gap-2 ${open?.id === a.id ? 'bg-brand-50 text-brand-700 font-medium' : 'text-gray-600 hover:bg-gray-100'}`}>
                      <span>{a.title}</span>
                      {open?.id === a.id && <ChevronRight className="w-3.5 h-3.5 shrink-0" />}
                    </button>
                  ))}
                </div>
              </div>
            )
          })}
          {results.length === 0 && <p className="text-sm text-gray-400 px-2">No matches for “{query}”.</p>}
        </nav>

        {/* Article */}
        <article className="card p-5 sm:p-6 min-h-[300px]">
          {open ? (
            <>
              <p className="text-[11px] font-semibold text-brand-600 uppercase tracking-wide">{open.category}</p>
              <Markdown source={open.body} />
              {query && <RelatedHint article={open} query={query} />}
            </>
          ) : (
            <p className="text-sm text-gray-400">Pick a topic on the left, or search above.</p>
          )}
        </article>
      </div>
    </div>
  )
}

function RelatedHint({ article, query }: { article: Article; query: string }) {
  const others = ARTICLES.filter(a => a.id !== article.id && (a.title.toLowerCase().includes(query.toLowerCase()) || a.keywords.some(k => k.includes(query.toLowerCase())))).slice(0, 4)
  if (!others.length) return null
  return (
    <div className="mt-6 pt-4 border-t border-gray-100">
      <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Related</p>
      <div className="flex flex-wrap gap-1.5">
        {others.map(o => <span key={o.id} className="text-[11px] text-gray-500">{o.title}</span>)}
      </div>
    </div>
  )
}
